// CANONICAL COPY (GH #7115). src/constants/frameworks/cra.js re-exports this file so the browser and the
// Function App share one compliance engine — one definition of "compliant". Edit here, never there.
'use strict';

// CRA — Regulation (EU) 2024/2847 — Cyber Resilience Act.
// Horizontal cybersecurity requirements for products with digital elements (PDEs).
// Source: Regulation (EU) 2024/2847 (CELEX 32024R2847, OJ L of 20 November 2024).
// Canonical control list mirrored inline in GitHub issue #1510.
//
// externalId scheme:
//   - `CRA-ART<n>-<sub>`     for operative articles (paragraph-level)
//   - `CRA-ANXI-<n>`         for Annex I Part I §1.x (1, 2A..2M)
//   - `CRA-ANXI-3-<n>`       for Annex I Part II vulnerability handling (1..8)
//   - `CRA-ANXII-<n>`        for Annex II user-instruction items
//
// Applicability: CRA is product regulation (manufacturers, importers,
// distributors). Boundary CRA-B-001 enforces per-tenant opt-in — this
// framework's `enabled` flag defaults to false and is toggled by super-admin
// per tenant, not by tenant admins.
//
// Multiple mappings with the same externalId are intentional — the last entry
// per externalId is the "primary" TCF for cross-reference display (marked
// `// primary`); all entries contribute to controlToFrameworks and externalRefs.

const FRAMEWORK_CRA = {
  id: 'fw-cra',
  type: 'law',
  name: 'Cyber Resilience Act',
  shortName: 'CRA',
  abbrev: 'CRA',
  description:
    'EU Regulation (EU) 2024/2847 — horizontal cybersecurity requirements for products with digital elements (PDEs). Binds manufacturers, importers and distributors. Vulnerability and incident reporting applies from 11 September 2026; full applicability 11 December 2027.',
  color: '#06b6d4',
  version: '2024/2847',
  jurisdiction: 'EU',
  sectionKeyDepth: 1,
  sections: [
    { key: 'ART13', name: 'Obligations of manufacturers' },
    { key: 'ART14', name: 'Reporting obligations of manufacturers' },
    { key: 'ART15', name: 'Other manufacturer obligations' },
    { key: 'ART16', name: 'Authorised representatives' },
    { key: 'ART19', name: 'Obligations of importers' },
    { key: 'ART20', name: 'Obligations of distributors' },
    { key: 'ART28', name: 'CE marking' },
    { key: 'ART29', name: 'EU declaration of conformity' },
    { key: 'ART32', name: 'Conformity assessment procedures' },
    { key: 'ANXI-1', name: 'Annex I Part I — Cybersecurity properties of products' },
    { key: 'ANXI-2', name: 'Annex I Part II — Vulnerability handling requirements' },
    { key: 'ANXII', name: 'Annex II — Information and instructions to users' },
  ],
  mappings: [
    // ── Article 13 — Obligations of manufacturers ─────────────────────────

    // 13(1) Place on the market only PDEs meeting Annex I essential requirements
    { externalId: 'CRA-ART13-1', section: 'ART13', scfId: 'SCF-MSS-004', ref: 'Art. 13(1)' },
    { externalId: 'CRA-ART13-1', section: 'ART13', scfId: 'SCF-DEV-003', ref: 'Art. 13(1)' },
    {
      primary: true,
      externalId: 'CRA-ART13-1',
      section: 'ART13',
      scfId: 'SCF-DEV-001',
      ref: 'Art. 13(1)',
    }, // primary

    // 13(2) Cybersecurity risk assessment per PDE
    { externalId: 'CRA-ART13-2', section: 'ART13', scfId: 'SCF-RSK-002', ref: 'Art. 13(2)' },
    {
      primary: true,
      externalId: 'CRA-ART13-2',
      section: 'ART13',
      scfId: 'SCF-RSK-001',
      ref: 'Art. 13(2)',
    }, // primary

    // 13(3) Due diligence on third-party components
    { externalId: 'CRA-ART13-3', section: 'ART13', scfId: 'SCF-VDR-001', ref: 'Art. 13(3)' },
    { externalId: 'CRA-ART13-3', section: 'ART13', scfId: 'SCF-VDR-002', ref: 'Art. 13(3)' },
    {
      primary: true,
      externalId: 'CRA-ART13-3',
      section: 'ART13',
      scfId: 'SCF-VDR-003',
      ref: 'Art. 13(3)',
    }, // primary

    // 13(4) Document cybersecurity aspects in technical documentation (Annex VII)
    { externalId: 'CRA-ART13-4', section: 'ART13', scfId: 'SCF-DEV-001', ref: 'Art. 13(4)' },
    {
      primary: true,
      externalId: 'CRA-ART13-4',
      section: 'ART13',
      scfId: 'SCF-GOV-011',
      ref: 'Art. 13(4)',
    }, // primary

    // 13(5) SBOM — machine-readable, top-level dependencies
    { externalId: 'CRA-ART13-5', section: 'ART13', scfId: 'SCF-AST-001', ref: 'Art. 13(5)' },
    {
      primary: true,
      externalId: 'CRA-ART13-5',
      section: 'ART13',
      scfId: 'SCF-VDR-003',
      ref: 'Art. 13(5)',
    }, // primary

    // 13(6) Apply vulnerability handling per Annex I Part II during support period
    { externalId: 'CRA-ART13-6', section: 'ART13', scfId: 'SCF-OPS-004', ref: 'Art. 13(6)' }, // primary

    // 13(7) Re-perform conformity assessment for substantial modifications
    { externalId: 'CRA-ART13-7', section: 'ART13', scfId: 'SCF-AUD-002', ref: 'Art. 13(7)' },
    {
      primary: true,
      externalId: 'CRA-ART13-7',
      section: 'ART13',
      scfId: 'SCF-DEV-008',
      ref: 'Art. 13(7)',
    }, // primary

    // 13(8) Determine and disclose support period (≥5y default)
    { externalId: 'CRA-ART13-8', section: 'ART13', scfId: 'SCF-MSS-004', ref: 'Art. 13(8)' },
    {
      primary: true,
      externalId: 'CRA-ART13-8',
      section: 'ART13',
      scfId: 'SCF-DEV-008',
      ref: 'Art. 13(8)',
    }, // primary

    // 13(9) Cooperate with market-surveillance authorities
    { externalId: 'CRA-ART13-9', section: 'ART13', scfId: 'SCF-GOV-005', ref: 'Art. 13(9)' }, // primary

    // 13(10) Security updates provided separately from functional updates
    { externalId: 'CRA-ART13-10', section: 'ART13', scfId: 'SCF-OPS-005', ref: 'Art. 13(10)' },
    {
      primary: true,
      externalId: 'CRA-ART13-10',
      section: 'ART13',
      scfId: 'SCF-DEV-008',
      ref: 'Art. 13(10)',
    }, // primary

    // 13(11) Make tech docs available after end-of-support
    { externalId: 'CRA-ART13-11', section: 'ART13', scfId: 'SCF-GOV-011', ref: 'Art. 13(11)' }, // primary

    // 13(12) Coordinated vulnerability disclosure policy + single point of contact
    { externalId: 'CRA-ART13-12', section: 'ART13', scfId: 'SCF-INC-001', ref: 'Art. 13(12)' },
    { externalId: 'CRA-ART13-12', section: 'ART13', scfId: 'SCF-GOV-005', ref: 'Art. 13(12)' },
    {
      primary: true,
      externalId: 'CRA-ART13-12',
      section: 'ART13',
      scfId: 'SCF-PPL-008',
      ref: 'Art. 13(12)',
    }, // primary — user/researcher reporting channel, not authority contact

    // 13(13) Internal sharing of vulnerability info
    { externalId: 'CRA-ART13-13', section: 'ART13', scfId: 'SCF-PPL-008', ref: 'Art. 13(13)' }, // primary

    // 13(14) Share vuln info on common third-party components with other manufacturers
    { externalId: 'CRA-ART13-14', section: 'ART13', scfId: 'SCF-GOV-006', ref: 'Art. 13(14)' },
    {
      primary: true,
      externalId: 'CRA-ART13-14',
      section: 'ART13',
      scfId: 'SCF-VDR-003',
      ref: 'Art. 13(14)',
    }, // primary

    // 13(15) Distribute security updates without delay, free of charge, with advisory
    { externalId: 'CRA-ART13-15', section: 'ART13', scfId: 'SCF-OPS-005', ref: 'Art. 13(15)' },
    {
      primary: true,
      externalId: 'CRA-ART13-15',
      section: 'ART13',
      scfId: 'SCF-DEV-008',
      ref: 'Art. 13(15)',
    }, // primary

    // 13(16) Provide instructions and information to users (Annex II)
    { externalId: 'CRA-ART13-16', section: 'ART13', scfId: 'SCF-GOV-011', ref: 'Art. 13(16)' }, // primary

    // 13(17) Contact address for users to report incidents/vulnerabilities
    { externalId: 'CRA-ART13-17', section: 'ART13', scfId: 'SCF-GOV-005', ref: 'Art. 13(17)' },
    {
      primary: true,
      externalId: 'CRA-ART13-17',
      section: 'ART13',
      scfId: 'SCF-PPL-008',
      ref: 'Art. 13(17)',
    }, // primary — user/researcher reporting channel, not authority contact

    // 13(18) Keep tech docs + EU DoC for 10 years
    { externalId: 'CRA-ART13-18', section: 'ART13', scfId: 'SCF-GOV-011', ref: 'Art. 13(18)' }, // primary

    // 13(19) End-of-life notice — inform users in advance, publish EoL
    { externalId: 'CRA-ART13-19', section: 'ART13', scfId: 'SCF-GOV-011', ref: 'Art. 13(19)' },
    {
      primary: true,
      externalId: 'CRA-ART13-19',
      section: 'ART13',
      scfId: 'SCF-DEV-008',
      ref: 'Art. 13(19)',
    }, // primary

    // 13(20) Public vulnerability reporting channel discoverable by users
    { externalId: 'CRA-ART13-20', section: 'ART13', scfId: 'SCF-GOV-005', ref: 'Art. 13(20)' },
    {
      primary: true,
      externalId: 'CRA-ART13-20',
      section: 'ART13',
      scfId: 'SCF-PPL-008',
      ref: 'Art. 13(20)',
    }, // primary

    // ── Article 14 — Reporting obligations of manufacturers ───────────────

    // 14(1) Early warning to CSIRT and ENISA within 24h of awareness
    { externalId: 'CRA-ART14-1', section: 'ART14', scfId: 'SCF-INC-001', ref: 'Art. 14(1)' },
    {
      primary: true,
      externalId: 'CRA-ART14-1',
      section: 'ART14',
      scfId: 'SCF-INC-006',
      ref: 'Art. 14(1)',
    }, // primary

    // 14(2) Vulnerability notification within 72h + final report within 14 days
    { externalId: 'CRA-ART14-2', section: 'ART14', scfId: 'SCF-INC-002', ref: 'Art. 14(2)' },
    { externalId: 'CRA-ART14-2', section: 'ART14', scfId: 'SCF-INC-003', ref: 'Art. 14(2)' },
    {
      primary: true,
      externalId: 'CRA-ART14-2',
      section: 'ART14',
      scfId: 'SCF-INC-006',
      ref: 'Art. 14(2)',
    }, // primary

    // 14(3) Severe incident report — same 24h / 72h / 14d cadence
    { externalId: 'CRA-ART14-3', section: 'ART14', scfId: 'SCF-INC-002', ref: 'Art. 14(3)' },
    { externalId: 'CRA-ART14-3', section: 'ART14', scfId: 'SCF-INC-003', ref: 'Art. 14(3)' },
    {
      primary: true,
      externalId: 'CRA-ART14-3',
      section: 'ART14',
      scfId: 'SCF-INC-006',
      ref: 'Art. 14(3)',
    }, // primary

    // 14(8) Inform users of vulnerability/incident + corrective measures
    { externalId: 'CRA-ART14-8', section: 'ART14', scfId: 'SCF-INC-004', ref: 'Art. 14(8)' },
    { externalId: 'CRA-ART14-8', section: 'ART14', scfId: 'SCF-PPL-008', ref: 'Art. 14(8)' },
    {
      primary: true,
      externalId: 'CRA-ART14-8',
      section: 'ART14',
      scfId: 'SCF-INC-003',
      ref: 'Art. 14(8)',
    }, // primary

    // ── Article 15 — Other manufacturer obligations ──────────────────────

    // 15(1) On reasoned request, provide info/docs to market surveillance authority
    { externalId: 'CRA-ART15-1', section: 'ART15', scfId: 'SCF-GOV-005', ref: 'Art. 15(1)' },
    {
      primary: true,
      externalId: 'CRA-ART15-1',
      section: 'ART15',
      scfId: 'SCF-AUD-002',
      ref: 'Art. 15(1)',
    }, // primary

    // 15(2) Cooperate with authorities on corrective action when non-compliant
    { externalId: 'CRA-ART15-2', section: 'ART15', scfId: 'SCF-GOV-005', ref: 'Art. 15(2)' },
    {
      primary: true,
      externalId: 'CRA-ART15-2',
      section: 'ART15',
      scfId: 'SCF-MSS-007',
      ref: 'Art. 15(2)',
    }, // primary

    // 15(3) Corrective action — recall or withdrawal
    { externalId: 'CRA-ART15-3', section: 'ART15', scfId: 'SCF-INC-003', ref: 'Art. 15(3)' },
    {
      primary: true,
      externalId: 'CRA-ART15-3',
      section: 'ART15',
      scfId: 'SCF-MSS-007',
      ref: 'Art. 15(3)',
    }, // primary

    // ── Article 16 — Authorised representatives ──────────────────────────

    // 16(1) Manufacturer outside EU appoints authorised representative
    { externalId: 'CRA-ART16-1', section: 'ART16', scfId: 'SCF-GOV-002', ref: 'Art. 16(1)' },
    {
      primary: true,
      externalId: 'CRA-ART16-1',
      section: 'ART16',
      scfId: 'SCF-VDR-002',
      ref: 'Art. 16(1)',
    }, // primary

    // 16(2) Rep keeps EU DoC + tech docs 10y; cooperates; terminates mandate if non-compliant
    { externalId: 'CRA-ART16-2', section: 'ART16', scfId: 'SCF-VDR-001', ref: 'Art. 16(2)' },
    {
      primary: true,
      externalId: 'CRA-ART16-2',
      section: 'ART16',
      scfId: 'SCF-GOV-011',
      ref: 'Art. 16(2)',
    }, // primary

    // ── Article 19 — Obligations of importers ────────────────────────────

    // 19(1) Place only compliant PDEs — verify CE marking, EU DoC, conformity assessment
    { externalId: 'CRA-ART19-1', section: 'ART19', scfId: 'SCF-VDR-002', ref: 'Art. 19(1)' },
    {
      primary: true,
      externalId: 'CRA-ART19-1',
      section: 'ART19',
      scfId: 'SCF-VDR-003',
      ref: 'Art. 19(1)',
    }, // primary

    // 19(2) Importer name + postal/electronic address on PDE/packaging/accompanying doc
    { externalId: 'CRA-ART19-2', section: 'ART19', scfId: 'SCF-AST-005', ref: 'Art. 19(2)' }, // primary

    // 19(3) Storage / transport conditions do not jeopardise conformity
    { externalId: 'CRA-ART19-3', section: 'ART19', scfId: 'SCF-AST-006', ref: 'Art. 19(3)' },
    {
      primary: true,
      externalId: 'CRA-ART19-3',
      section: 'ART19',
      scfId: 'SCF-OPS-005',
      ref: 'Art. 19(3)',
    }, // primary

    // 19(4) Inform manufacturer + MSA of significant cybersecurity risk
    { externalId: 'CRA-ART19-4', section: 'ART19', scfId: 'SCF-INC-003', ref: 'Art. 19(4)' },
    {
      primary: true,
      externalId: 'CRA-ART19-4',
      section: 'ART19',
      scfId: 'SCF-GOV-005',
      ref: 'Art. 19(4)',
    }, // primary

    // ── Article 20 — Obligations of distributors ─────────────────────────

    // 20(1) Due care — verify CE marking + required documentation
    { externalId: 'CRA-ART20-1', section: 'ART20', scfId: 'SCF-VDR-001', ref: 'Art. 20(1)' },
    {
      primary: true,
      externalId: 'CRA-ART20-1',
      section: 'ART20',
      scfId: 'SCF-VDR-003',
      ref: 'Art. 20(1)',
    }, // primary

    // 20(2) Storage / transport conditions
    { externalId: 'CRA-ART20-2', section: 'ART20', scfId: 'SCF-AST-006', ref: 'Art. 20(2)' },
    {
      primary: true,
      externalId: 'CRA-ART20-2',
      section: 'ART20',
      scfId: 'SCF-OPS-005',
      ref: 'Art. 20(2)',
    }, // primary

    // 20(3) Inform manufacturer + authorities of cybersecurity risk
    { externalId: 'CRA-ART20-3', section: 'ART20', scfId: 'SCF-INC-003', ref: 'Art. 20(3)' },
    {
      primary: true,
      externalId: 'CRA-ART20-3',
      section: 'ART20',
      scfId: 'SCF-GOV-005',
      ref: 'Art. 20(3)',
    }, // primary

    // ── Chapter III — Conformity (selected) ──────────────────────────────

    // 28 Affix CE marking
    { externalId: 'CRA-ART28', section: 'ART28', scfId: 'SCF-AST-005', ref: 'Art. 28' }, // primary

    // 29 Draw up EU Declaration of Conformity
    { externalId: 'CRA-ART29', section: 'ART29', scfId: 'SCF-GOV-011', ref: 'Art. 29' }, // primary

    // 32 Conformity assessment procedures
    { externalId: 'CRA-ART32', section: 'ART32', scfId: 'SCF-AUD-001', ref: 'Art. 32' },
    {
      primary: true,
      externalId: 'CRA-ART32',
      section: 'ART32',
      scfId: 'SCF-AUD-002',
      ref: 'Art. 32',
    }, // primary

    // ── Annex I Part I — Cybersecurity properties of products ─────────────

    // 1.1 PDE designed/developed/produced to ensure appropriate cybersecurity based on risks
    {
      externalId: 'CRA-ANXI-1',
      section: 'ANXI-1',
      scfId: 'SCF-DEV-001',
      ref: 'Annex I, Part I §1.1',
    },
    {
      primary: true,
      externalId: 'CRA-ANXI-1',
      section: 'ANXI-1',
      scfId: 'SCF-DEV-003',
      ref: 'Annex I, Part I §1.1',
    }, // primary

    // 1.2(a) Delivered without known exploitable vulnerabilities
    {
      externalId: 'CRA-ANXI-2A',
      section: 'ANXI-1',
      scfId: 'SCF-DEV-005',
      ref: 'Annex I, Part I §1.2(a)',
    },
    {
      primary: true,
      externalId: 'CRA-ANXI-2A',
      section: 'ANXI-1',
      scfId: 'SCF-OPS-004',
      ref: 'Annex I, Part I §1.2(a)',
    }, // primary

    // 1.2(b) Secure-by-default configuration with reset capability
    {
      externalId: 'CRA-ANXI-2B',
      section: 'ANXI-1',
      scfId: 'SCF-OPS-005',
      ref: 'Annex I, Part I §1.2(b)',
    }, // primary

    // 1.2(c) Vulnerabilities addressable via security updates; automatic where appropriate
    {
      externalId: 'CRA-ANXI-2C',
      section: 'ANXI-1',
      scfId: 'SCF-OPS-004',
      ref: 'Annex I, Part I §1.2(c)',
    },
    {
      primary: true,
      externalId: 'CRA-ANXI-2C',
      section: 'ANXI-1',
      scfId: 'SCF-DEV-008',
      ref: 'Annex I, Part I §1.2(c)',
    }, // primary

    // 1.2(d) Protection from unauthorised access — authentication/identity/access mgmt
    {
      externalId: 'CRA-ANXI-2D',
      section: 'ANXI-1',
      scfId: 'SCF-IAM-008',
      ref: 'Annex I, Part I §1.2(d)',
    },
    {
      externalId: 'CRA-ANXI-2D',
      section: 'ANXI-1',
      scfId: 'SCF-IAM-002',
      ref: 'Annex I, Part I §1.2(d)',
    },
    {
      primary: true,
      externalId: 'CRA-ANXI-2D',
      section: 'ANXI-1',
      scfId: 'SCF-IAM-001',
      ref: 'Annex I, Part I §1.2(d)',
    }, // primary

    // 1.2(e) Protect confidentiality — encryption at rest + in transit
    {
      externalId: 'CRA-ANXI-2E',
      section: 'ANXI-1',
      scfId: 'SCF-DAT-001',
      ref: 'Annex I, Part I §1.2(e)',
    },
    {
      primary: true,
      externalId: 'CRA-ANXI-2E',
      section: 'ANXI-1',
      scfId: 'SCF-NET-005',
      ref: 'Annex I, Part I §1.2(e)',
    }, // primary

    // 1.2(f) Protect integrity of data, commands, programs, configurations
    {
      externalId: 'CRA-ANXI-2F',
      section: 'ANXI-1',
      scfId: 'SCF-NET-005',
      ref: 'Annex I, Part I §1.2(f)',
    },
    {
      primary: true,
      externalId: 'CRA-ANXI-2F',
      section: 'ANXI-1',
      scfId: 'SCF-OPS-005',
      ref: 'Annex I, Part I §1.2(f)',
    }, // primary

    // 1.2(g) Data minimisation — process only adequate/relevant/limited data
    {
      externalId: 'CRA-ANXI-2G',
      section: 'ANXI-1',
      scfId: 'SCF-DAT-002',
      ref: 'Annex I, Part I §1.2(g)',
    },
    {
      primary: true,
      externalId: 'CRA-ANXI-2G',
      section: 'ANXI-1',
      scfId: 'SCF-PRV-007',
      ref: 'Annex I, Part I §1.2(g)',
    }, // primary

    // 1.2(h) Availability — resilience against DoS attacks
    {
      externalId: 'CRA-ANXI-2H',
      section: 'ANXI-1',
      scfId: 'SCF-OPS-011',
      ref: 'Annex I, Part I §1.2(h)',
    },
    {
      primary: true,
      externalId: 'CRA-ANXI-2H',
      section: 'ANXI-1',
      scfId: 'SCF-BCM-002',
      ref: 'Annex I, Part I §1.2(h)',
    }, // primary

    // 1.2(i) Minimise negative impact on availability of other devices/networks
    {
      externalId: 'CRA-ANXI-2I',
      section: 'ANXI-1',
      scfId: 'SCF-NET-001',
      ref: 'Annex I, Part I §1.2(i)',
    },
    {
      primary: true,
      externalId: 'CRA-ANXI-2I',
      section: 'ANXI-1',
      scfId: 'SCF-NET-003',
      ref: 'Annex I, Part I §1.2(i)',
    }, // primary

    // 1.2(j) Limit attack surfaces, including external interfaces
    {
      externalId: 'CRA-ANXI-2J',
      section: 'ANXI-1',
      scfId: 'SCF-DEV-003',
      ref: 'Annex I, Part I §1.2(j)',
    },
    {
      primary: true,
      externalId: 'CRA-ANXI-2J',
      section: 'ANXI-1',
      scfId: 'SCF-DEV-004',
      ref: 'Annex I, Part I §1.2(j)',
    }, // primary

    // 1.2(k) Reduce impact of incidents via exploitation-mitigation mechanisms
    {
      externalId: 'CRA-ANXI-2K',
      section: 'ANXI-1',
      scfId: 'SCF-DEV-004',
      ref: 'Annex I, Part I §1.2(k)',
    },
    {
      primary: true,
      externalId: 'CRA-ANXI-2K',
      section: 'ANXI-1',
      scfId: 'SCF-INC-003',
      ref: 'Annex I, Part I §1.2(k)',
    }, // primary

    // 1.2(l) Security-related info via logging
    {
      externalId: 'CRA-ANXI-2L',
      section: 'ANXI-1',
      scfId: 'SCF-OPS-007',
      ref: 'Annex I, Part I §1.2(l)',
    },
    {
      primary: true,
      externalId: 'CRA-ANXI-2L',
      section: 'ANXI-1',
      scfId: 'SCF-OPS-006',
      ref: 'Annex I, Part I §1.2(l)',
    }, // primary

    // 1.2(m) Users can securely erase or transfer their data
    {
      externalId: 'CRA-ANXI-2M',
      section: 'ANXI-1',
      scfId: 'SCF-DAT-006',
      ref: 'Annex I, Part I §1.2(m)',
    }, // transfer half — data portability and transfer (secondary)
    {
      primary: true,
      externalId: 'CRA-ANXI-2M',
      section: 'ANXI-1',
      scfId: 'SCF-DAT-003',
      ref: 'Annex I, Part I §1.2(m)',
    }, // primary — erase half (information deletion)

    // ── Annex I Part II — Vulnerability handling requirements ────────────

    // 2.1 Identify & document components and vulnerabilities + SBOM
    {
      externalId: 'CRA-ANXI-3-1',
      section: 'ANXI-2',
      scfId: 'SCF-AST-001',
      ref: 'Annex I, Part II §1',
    },
    {
      externalId: 'CRA-ANXI-3-1',
      section: 'ANXI-2',
      scfId: 'SCF-OPS-004',
      ref: 'Annex I, Part II §1',
    },
    {
      primary: true,
      externalId: 'CRA-ANXI-3-1',
      section: 'ANXI-2',
      scfId: 'SCF-VDR-003',
      ref: 'Annex I, Part II §1',
    }, // primary

    // 2.2 Address & remediate vulns without delay; security updates separate from features
    {
      externalId: 'CRA-ANXI-3-2',
      section: 'ANXI-2',
      scfId: 'SCF-OPS-004',
      ref: 'Annex I, Part II §2',
    },
    {
      primary: true,
      externalId: 'CRA-ANXI-3-2',
      section: 'ANXI-2',
      scfId: 'SCF-DEV-008',
      ref: 'Annex I, Part II §2',
    }, // primary

    // 2.3 Effective and regular cybersecurity testing and reviews
    {
      externalId: 'CRA-ANXI-3-3',
      section: 'ANXI-2',
      scfId: 'SCF-AUD-003',
      ref: 'Annex I, Part II §3',
    },
    {
      externalId: 'CRA-ANXI-3-3',
      section: 'ANXI-2',
      scfId: 'SCF-AUD-004',
      ref: 'Annex I, Part II §3',
    },
    {
      primary: true,
      externalId: 'CRA-ANXI-3-3',
      section: 'ANXI-2',
      scfId: 'SCF-DEV-005',
      ref: 'Annex I, Part II §3',
    }, // primary

    // 2.4 Once update available, share and publicly disclose fixed vulnerabilities
    {
      externalId: 'CRA-ANXI-3-4',
      section: 'ANXI-2',
      scfId: 'SCF-OPS-004',
      ref: 'Annex I, Part II §4',
    },
    {
      primary: true,
      externalId: 'CRA-ANXI-3-4',
      section: 'ANXI-2',
      scfId: 'SCF-INC-003',
      ref: 'Annex I, Part II §4',
    }, // primary

    // 2.5 Put in place a coordinated vulnerability disclosure policy
    {
      externalId: 'CRA-ANXI-3-5',
      section: 'ANXI-2',
      scfId: 'SCF-PPL-008',
      ref: 'Annex I, Part II §5',
    },
    {
      primary: true,
      externalId: 'CRA-ANXI-3-5',
      section: 'ANXI-2',
      scfId: 'SCF-INC-001',
      ref: 'Annex I, Part II §5',
    }, // primary

    // 2.6 Facilitate sharing of potential vulnerabilities; provide contact for reporting
    {
      externalId: 'CRA-ANXI-3-6',
      section: 'ANXI-2',
      scfId: 'SCF-PPL-008',
      ref: 'Annex I, Part II §6',
    }, // primary

    // 2.7 Mechanisms to securely distribute updates; automatic where applicable
    {
      externalId: 'CRA-ANXI-3-7',
      section: 'ANXI-2',
      scfId: 'SCF-OPS-005',
      ref: 'Annex I, Part II §7',
    },
    {
      externalId: 'CRA-ANXI-3-7',
      section: 'ANXI-2',
      scfId: 'SCF-DEV-008',
      ref: 'Annex I, Part II §7',
    },
    {
      primary: true,
      externalId: 'CRA-ANXI-3-7',
      section: 'ANXI-2',
      scfId: 'SCF-NET-005',
      ref: 'Annex I, Part II §7',
    }, // primary

    // 2.8 Patches disseminated without delay, free of charge, with advisory
    {
      externalId: 'CRA-ANXI-3-8',
      section: 'ANXI-2',
      scfId: 'SCF-OPS-005',
      ref: 'Annex I, Part II §8',
    },
    {
      primary: true,
      externalId: 'CRA-ANXI-3-8',
      section: 'ANXI-2',
      scfId: 'SCF-DEV-008',
      ref: 'Annex I, Part II §8',
    }, // primary

    // ── Annex II — Information and instructions to users ─────────────────

    // II-1 Manufacturer name, postal address and contact details
    { externalId: 'CRA-ANXII-1', section: 'ANXII', scfId: 'SCF-GOV-011', ref: 'Annex II §1' }, // primary

    // II-2 Type/batch/serial identifier
    { externalId: 'CRA-ANXII-2', section: 'ANXII', scfId: 'SCF-AST-005', ref: 'Annex II §2' }, // primary

    // II-3 Intended purpose + main cybersecurity characteristics
    { externalId: 'CRA-ANXII-3', section: 'ANXII', scfId: 'SCF-GOV-011', ref: 'Annex II §3' }, // primary

    // II-4 URL of EU declaration of conformity
    { externalId: 'CRA-ANXII-4', section: 'ANXII', scfId: 'SCF-GOV-011', ref: 'Annex II §4' }, // primary

    // II-5 Type of technical security support + support period
    { externalId: 'CRA-ANXII-5', section: 'ANXII', scfId: 'SCF-DEV-008', ref: 'Annex II §5' },
    {
      primary: true,
      externalId: 'CRA-ANXII-5',
      section: 'ANXII',
      scfId: 'SCF-GOV-011',
      ref: 'Annex II §5',
    }, // primary

    // II-6 Instructions for secure use, configurations, updates, recommended auth
    { externalId: 'CRA-ANXII-6', section: 'ANXII', scfId: 'SCF-PPL-003', ref: 'Annex II §6' },
    { externalId: 'CRA-ANXII-6', section: 'ANXII', scfId: 'SCF-IAM-008', ref: 'Annex II §6' },
    {
      primary: true,
      externalId: 'CRA-ANXII-6',
      section: 'ANXII',
      scfId: 'SCF-GOV-011',
      ref: 'Annex II §6',
    }, // primary

    // II-7 Instructions on disposal at end-of-life
    { externalId: 'CRA-ANXII-7', section: 'ANXII', scfId: 'SCF-PHY-014', ref: 'Annex II §7' },
    {
      primary: true,
      externalId: 'CRA-ANXII-7',
      section: 'ANXII',
      scfId: 'SCF-DAT-003',
      ref: 'Annex II §7',
    }, // primary
  ],
};

module.exports = {
  FRAMEWORK_CRA,
};
